import React, { useState } from 'react';
import { Icon } from '@iconify/react';
import { validatePassword, validateConfirmPassword, validateForm } from '../../../utils/validation';
import { toast } from 'react-toastify';
import { changePassword } from '../../../services/userService';

function ChangePassword() {
  const [formData, setFormData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  const [passwordVisible, setPasswordVisible] = useState({
    currentPassword: false,
    newPassword: false,
    confirmPassword: false
  });

  const [passwordRequirements, setPasswordRequirements] = useState({
    length: false,
    letters: false,
    numbers: false,
    special: false
  });

  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);

  const validationRules = {
    currentPassword: (value) => value ? [] : ['Current password is required'],
    newPassword: validatePassword,
    confirmPassword: (value) => validateConfirmPassword(value, formData.newPassword)
  };

  const checkPasswordRequirements = (password) => ({
    length: password.length >= 8,
    letters: /[a-zA-Z]/.test(password),
    numbers: /[0-9]/.test(password),
    special: /[!@#$%^&*(),.?":{}|<>]/.test(password)
  });

  const handleChange = (e) => {
    const { id, value } = e.target;
    const newFormData = { ...formData, [id]: value };
    const newErrors = { ...errors, [id]: undefined };

    if (id === 'newPassword') {
      setPasswordRequirements(checkPasswordRequirements(value));
      if (newFormData.confirmPassword) {
        const confirmErrors = validateConfirmPassword(newFormData.confirmPassword, value);
        if (confirmErrors.length > 0) {
          newErrors.confirmPassword = confirmErrors;
        }
      }
    }

    if (id === 'confirmPassword') {
      const confirmErrors = validateConfirmPassword(value, newFormData.newPassword);
      if (confirmErrors.length > 0) {
        newErrors.confirmPassword = confirmErrors;
      }
    }

    setFormData(newFormData);
    setErrors(newErrors);
  };

  const togglePasswordVisibility = (field) => {
    setPasswordVisible(prev => ({ ...prev, [field]: !prev[field] }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const validation = validateForm(formData, validationRules);
    if (!validation.isValid) {
      setErrors(validation.errors);
      return;
    }

    setIsLoading(true);

    try {
      const response = await changePassword({
        currentPassword: formData.currentPassword,
        newPassword: formData.newPassword
      });

      if (response.success) {
        toast.success(response.message || response?.data?.message || 'Password updated successfully!');
        setFormData({
          currentPassword: '',
          newPassword: '',
          confirmPassword: ''
        });
        setErrors({});
      } else {
        toast.error(
          response?.data?.error_msg ||
          response?.message ||
          'Current password is invalid.'
        );
      }
    } catch (error) {
      console.error("❌ Error changing password:", error);
      toast.error(
        error.response?.data?.data?.error_msg ||
        error.response?.data?.error_msg ||
        error.response?.data?.message ||
        "Something went wrong"
      );
    } finally {
      setIsLoading(false);
    }
  };

  const renderInputField = (label, id, value, visible, onChange, error, placeholder) => (
    <div className="md:w-full mb-3">
      <div className="profile-field-group">
        <label className="block text-sm font-medium text-font-primary mb-1.5">{label}</label>
        <div className="profile-field relative">
          <input
            type={visible ? "text" : "password"}
            className={`w-full px-3 py-2 border border-input-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 pr-12 ${error ? 'border-red-500' : ''}`}
            id={id}
            value={value}
            onChange={onChange}
            placeholder={placeholder}
            disabled={isLoading}
          />
          <button
            type="button"
            className="absolute right-3 top-1/2 -translate-y-1/2 bg-transparent border-none z-10"
            onClick={() => togglePasswordVisibility(id)}
            disabled={isLoading}
          >
            <Icon icon={visible ? "mdi:eye-off" : "mdi:eye"} width="18" height="18" />
          </button>
          {error && <div className="text-red-500 text-sm mt-1 block">{error[0]}</div>}
        </div>
      </div>
    </div>
  );

  return (
    <div className="profile-container">
      <div className="bg-white rounded-lg shadow-sm border border-gray-100">
        <div className="profile-card-header">
          <h5>Change Password</h5>
        </div>
        <div className="p-4">
          <form onSubmit={handleSubmit}>
            <div className="flex">
              <div className="w-full md:w-1/2">

                {renderInputField(
                  "Current Password",
                  "currentPassword",
                  formData.currentPassword,
                  passwordVisible.currentPassword,
                  handleChange,
                  errors.currentPassword,
                  "Enter current password"
                )}

                {renderInputField(
                  "New Password",
                  "newPassword",
                  formData.newPassword,
                  passwordVisible.newPassword,
                  handleChange,
                  errors.newPassword,
                  "Enter new password"
                )}

                {/* Password Requirements */}
                <div className="password-requirements mt-2">
                  <div className={`requirement-badge ${passwordRequirements.length ? 'valid' : ''}`}>
                    <Icon icon={passwordRequirements.length ? "mdi:check" : "mdi:close"} className="icon" /> 8+ Characters
                  </div>
                  <div className={`requirement-badge ${passwordRequirements.letters ? 'valid' : ''}`}>
                    <Icon icon={passwordRequirements.letters ? "mdi:check" : "mdi:close"} className="icon" /> Letters
                  </div>
                  <div className={`requirement-badge ${passwordRequirements.numbers ? 'valid' : ''}`}>
                    <Icon icon={passwordRequirements.numbers ? "mdi:check" : "mdi:close"} className="icon" /> Numbers
                  </div>
                  <div className={`requirement-badge ${passwordRequirements.special ? 'valid' : ''}`}>
                    <Icon icon={passwordRequirements.special ? "mdi:check" : "mdi:close"} className="icon" /> Special Chars
                  </div>
                </div>

                {renderInputField(
                  "Confirm New Password",
                  "confirmPassword",
                  formData.confirmPassword,
                  passwordVisible.confirmPassword,
                  handleChange,
                  errors.confirmPassword,
                  "Confirm new password"
                )}

                {/* Submit */}
                <div className="w-full mt-3 flex justify-center">
                  <button type="submit" className={`bg-primary hover:bg-primary/90 text-white font-medium py-2 px-4 rounded-lg w-1/2 ${isLoading ? 'loading' : ''}`} disabled={isLoading}>
                    {isLoading ? (
                      <>
                        <div className="inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" role="status" />
                        Changing Password...
                      </>
                    ) : 'Change Password'}
                  </button>
                </div>

              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

export default ChangePassword;
